/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { instructions } from "./Html/instructions";
import { modal } from "commonAssets/lib/modal";
import Tagify from "@yaireo/tagify";
import "@yaireo/tagify/dist/tagify.css";
import { validateField } from "commonAssets/lib/validateField";

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    console.log("[Initiate claim]...");

    /**
     * Dom updates
     */

    // First and Last Name container
    const [firstName, lastName] = document.querySelectorAll(
        "#first_name, #last_name"
    );
    firstName.disabled = Boolean(firstName.value);
    lastName.disabled = Boolean(lastName.value);

    // Paper-claim entry (Ops): allow editing the signature Date field.
    const isPaperClaim =
        new URLSearchParams(window.location.search).get("paper_claim") ===
        "true";
    if (isPaperClaim) {
        const dateField = document.getElementById("date");
        if (dateField) dateField.disabled = false;
    }

    // Update the instructions section with custom HTML
    document.querySelector("#generalInstructions > div").innerHTML =
        instructions;

    // Contact information styling
    document
        .querySelector("#contactInfoTitle")
        .classList.add("mb-3", "pt-5", "pb-5", "border-bottom");

    // Remove unnecessary elements from the claim form
    document.querySelector("#claimFieldsTitle").remove();
    document.querySelector("#claimFieldsBody").remove();
    document.querySelectorAll("caption").forEach((caption) => {
        caption.remove();
    });

    // Target the parent element of the add/remove transaction buttons
    let trxCount = 1; // this matches the first transaction table id
    document.querySelectorAll('[id^="add_transactions_"]').forEach((btn) => {
        btn.parentElement.classList.add("trx_ctrl");
        btn.parentElement.id = `trx_ctrl_${trxCount}`;
        btn.parentElement.setAttribute(
            "data-for",
            `table_responsive_${trxCount}`
        );
        trxCount++;
    });

    // Move transaction tables and buttons to their respective placeholders
    const trxController = {
        disability: {
            placeholder: document.querySelector(
                '[data-field-id="__wrapper_6"]'
            ),
            tableParent: document.querySelector("#table_responsive_1"),
            buttonsParent: document.querySelector("#trx_ctrl_1"),
        },
        application: {
            placeholder: document.querySelector(
                '[data-field-id="__wrapper_10"]'
            ),
            tableParent: document.querySelector("#table_responsive_2"),
            buttonsParent: document.querySelector("#trx_ctrl_2"),
        },
        employment: {
            placeholder: document.querySelector(
                '[data-field-id="__wrapper_55"]'
            ),
            tableParent: document.querySelector("#table_responsive_3"),
            buttonsParent: document.querySelector("#trx_ctrl_3"),
        },
        unemployment: {
            placeholder: document.querySelector(
                '[data-field-id="__wrapper_57"]'
            ),
            tableParent: document.querySelector("#table_responsive_4"),
            buttonsParent: document.querySelector("#trx_ctrl_4"),
        },
    };
    Object.keys(trxController).forEach((key) => {
        const { placeholder, tableParent, buttonsParent } = trxController[key];
        if (placeholder && tableParent && buttonsParent) {
            placeholder.innerHTML = ""; // clear existing content
            placeholder.classList.add("mt-3", "mb-5");
            placeholder.appendChild(tableParent);
            placeholder.appendChild(buttonsParent);
        } else {
            console.warn(`Missing elements for ${key} transaction controller`);
        }
    });
    document.querySelector("#transactions").remove();

    // START AGENT
    /**
     * Claim logic enhancements to align the online form with the paper form
     * requirements. Focus areas:
     * - Veteran eligibility gating and >= 30% rating enforcement
     * - Class period date bounds on BOP-related dates
     * - Conditional requiredness + show/hide for dependent questions
     * - Country-specific address logic uses core handler; no changes here
     * - Email confirmation uses core handler; no changes here
     *
     * Notes:
     * - Keep this section self-contained; avoid external imports.
     * - Use a small rule engine (“dynamic dispatch”) to avoid large if/switch.
     */

    // Utilities
    const $ = (sel, root = document) => root.querySelector(sel);
    const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

    const CLASS_MIN_DATE = "2018-08-20";
    const CLASS_MAX_DATE = "2025-06-17";

    // Helper text: Loss of Compensation callout below label
    try {
        const locContainer = document.querySelector(
            '[data-field-id="_textarea-loss-of-compensation"]'
        );
        const locLabel = document.querySelector(
            'label[for="textarea-loss-of-compensation"]'
        );
        if (
            locContainer &&
            locLabel &&
            !locContainer.querySelector("#loss-of-compensation-helper")
        ) {
            const helper = document.createElement("div");
            helper.id = "loss-of-compensation-helper";
            helper.className = "callout callout-alert";
            helper.innerHTML = `
                <div class="callout-body">
                    You can provide <span id="goToSupportingDocumentation">Supporting Documentation</span> for your claim on the widget below. Examples of supporting documentation may include W-2s, tax returns, job offer(s) reflecting a wage component, and/or pay stubs, etc.
                </div>
            `;
            // Insert immediately after the field label
            locLabel.insertAdjacentElement("afterend", helper);
        }
    } catch (e) {
        console.warn("Failed to inject Loss of Compensation helper text", e);
    }

    // Support link: Scroll to documentation upload widget
    try {
        const docLink = document.getElementById("goToSupportingDocumentation");
        if (docLink && !docLink.dataset.boundScroll) {
            docLink.style.cursor = "pointer";
            docLink.dataset.boundScroll = "true";
            docLink.addEventListener("click", (ev) => {
                ev.preventDefault();
                const target =
                    document.getElementById("documentation") ||
                    document.querySelector("#documentation");
                if (target && typeof target.scrollIntoView === "function") {
                    target.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            });
        }
    } catch (e) {
        console.warn(
            "Failed to bind Supporting Documentation scroll handler",
            e
        );
    }

    // Helper text: Earnings Difference info callout below label
    try {
        const edContainer = document.querySelector(
            '[data-field-id="_select-earnings-difference"]'
        );
        const edLabel = document.querySelector(
            'label[for="select-earnings-difference"]'
        );
        if (
            edContainer &&
            edLabel &&
            !edContainer.querySelector("#earnings-difference-helper")
        ) {
            const helper = document.createElement("div");
            helper.id = "earnings-difference-helper";
            helper.className = "callout callout-info";
            helper.innerHTML = `
                <div class="callout-body">
                    For example, did the BOP withdraw your conditional job offer because you failed to provide the medical information it required you to provide, it deemed you medically unfit, because you had bad credit, or for some other reason?
                </div>
            `;
            edLabel.insertAdjacentElement("afterend", helper);
        }
    } catch (e) {
        console.warn("Failed to inject Earnings Difference helper text", e);
    }

    // Helper text: BOP Reason for Termination info callout below label
    try {
        const brtContainer = document.querySelector(
            '[data-field-id="_textarea-bop-reason-for-termination"]'
        );
        const brtLabel = document.querySelector(
            'label[for="textarea-bop-reason-for-termination"]'
        );
        if (
            brtContainer &&
            brtLabel &&
            !brtContainer.querySelector("#bop-reason-termination-helper")
        ) {
            const helper = document.createElement("div");
            helper.id = "bop-reason-termination-helper";
            helper.className = "callout callout-info";
            helper.innerHTML = `
                <div class="callout-body">
                    For example, did the BOP terminate you because it deemed you medically unqualified? Or, as another example, did the BOP terminate you because it determined you had engaged in misconduct?
                </div>
            `;
            brtLabel.insertAdjacentElement("afterend", helper);
        }
    } catch (e) {
        console.warn(
            "Failed to inject BOP Reason for Termination helper text",
            e
        );
    }

    // Normalize callout sizing: apply Bootstrap's fs-6 to all callouts
    try {
        $$(".callout").forEach((el) => {
            if (!el.classList.contains("fs-6")) el.classList.add("fs-6");
        });
    } catch (e) {
        console.warn("Failed to apply fs-6 to callouts", e);
    }

    // Initialize Tagify on Branch of Military field to enforce allowed values
    try {
        const branchInput = document.getElementById(
            "select-branch-of-military"
        );
        if (branchInput) {
            // Update label copy to reflect multi-select (branch[es]) and sync data-label
            const branchLabel = document.querySelector(
                'label[for="select-branch-of-military"]'
            );
            const NEW_BRANCH_LABEL =
                "Please state the branch(es) of the military in which you served.";
            if (branchLabel) {
                const textNode = Array.from(branchLabel.childNodes).find(
                    (n) => n.nodeType === Node.TEXT_NODE
                );
                if (textNode) {
                    textNode.nodeValue = NEW_BRANCH_LABEL + " ";
                } else {
                    // Fallback: prepend text while preserving existing spans
                    branchLabel.insertBefore(
                        document.createTextNode(NEW_BRANCH_LABEL + " "),
                        branchLabel.firstChild
                    );
                }
            }
            branchInput.setAttribute("data-label", NEW_BRANCH_LABEL);

            const whitelist = [
                "ARMY",
                "MARINE CORPS",
                "NAVY",
                "AIR FORCE",
                "SPACE FORCE",
                "COAST GUARD",
            ];
            const tagify = new Tagify(branchInput, {
                whitelist,
                enforceWhitelist: true,
                // allow multiple branches
                delimiters: ",",
                dropdown: {
                    enabled: 0,
                    maxItems: whitelist.length,
                    closeOnSelect: true,
                },
                placeholder: "Select or type branch(es), then press Enter",
                // keep original casing from whitelist
                transformTag: (tagData) => {
                    if (tagData && tagData.value) {
                        const match = whitelist.find(
                            (w) =>
                                w.toUpperCase() ===
                                String(tagData.value).toUpperCase()
                        );
                        if (match) tagData.value = match;
                    }
                },
            });

            // Add helper text describing multi-select behavior and bind accessibility
            try {
                const container = document.querySelector(
                    '[data-field-id="_select-branch-of-military"]'
                );
                if (
                    container &&
                    !document.getElementById("branch-of-military-help")
                ) {
                    const help = document.createElement("div");
                    help.className = "form-text text-muted mt-1";
                    help.id = "branch-of-military-help";
                    help.textContent =
                        "You may select more than one branch. Type to search and press Enter after each.";
                    // Insert after the label for best context
                    if (branchLabel && branchLabel.parentNode === container) {
                        branchLabel.insertAdjacentElement("afterend", help);
                    } else {
                        container.appendChild(help);
                    }
                    const describedBy = (
                        branchInput.getAttribute("aria-describedby") || ""
                    )
                        .split(" ")
                        .filter(Boolean);
                    if (!describedBy.includes(help.id)) {
                        describedBy.push(help.id);
                        branchInput.setAttribute(
                            "aria-describedby",
                            describedBy.join(" ")
                        );
                    }
                }
            } catch (e) {
                console.warn("Branch helper text injection failed", e);
            }

            // Prevent label click from focusing the hidden original input; focus Tagify instead
            if (branchLabel) {
                branchLabel.addEventListener("click", (e) => {
                    e.preventDefault();
                    if (tagify && tagify.DOM && tagify.DOM.input) {
                        tagify.DOM.input.focus();
                    }
                });
            }

            // When clearing tags, ensure required validation triggers
            tagify.on("remove", () => {
                // Clear underlying value so required validation can fail
                branchInput.value = "";
                validateField(branchInput);
            });
            tagify.on("add", () => {
                validateField(branchInput);
            });

            // Store tagify instance for later access when populating saved data
            branchInput.tagify = tagify;
        }
    } catch (e) {
        console.warn("Tagify init for branch of military failed", e);
    }

    // Toggle required by updating both the DOM attribute and data-validate
    const setRequired = (el, required) => {
        if (!el) return;
        const parts = (el.getAttribute("data-validate") || "")
            .split(" ")
            .filter(Boolean);
        if (required) {
            if (!parts.includes("required")) parts.push("required");
            el.setAttribute("required", "");
        } else {
            el.removeAttribute("required");
            const idx = parts.indexOf("required");
            if (idx !== -1) parts.splice(idx, 1);
        }
        el.setAttribute("data-validate", parts.join(" "));
        updateLabelAsterisk(el, required);
    };

    // Add date-min/date-max to a date input
    const setDateBounds = (el, min, max) => {
        if (!el) return;
        el.setAttribute("data-date-min", min || "");
        el.setAttribute("data-date-max", max || "");
        const parts = (el.getAttribute("data-validate") || "")
            .split(" ")
            .filter(Boolean);
        if (!parts.includes("date")) parts.push("date");
        if (min && !parts.includes("date-min")) parts.push("date-min");
        if (max && !parts.includes("date-max")) parts.push("date-max");
        el.setAttribute("data-validate", parts.join(" "));
    };

    // Add/remove visible asterisk on labels for required fields
    const updateLabelAsterisk = (field, required) => {
        if (!field || !field.id) return;
        const label = document.querySelector(`label[for="${field.id}"]`);
        if (!label) return;
        const existing = label.querySelector(".required-asterisk");
        if (required) {
            if (!existing) {
                const star = document.createElement("span");
                star.className = "required-asterisk";
                star.setAttribute("aria-hidden", "true");
                star.textContent = " *";
                const err = label.querySelector("span.error");
                if (err && err.parentNode === label) {
                    label.insertBefore(star, err);
                } else {
                    label.appendChild(star);
                }
            }
        } else if (existing) {
            existing.remove();
        }
    };

    // Clear any existing validation error UI for a field
    const clearFieldError = (field) => {
        if (!field || !field.id) return;
        const label = document.querySelector(`label[for="${field.id}"]`);
        if (label) {
            label.classList.remove("show", "error");
            const span = label.querySelector("span.error");
            if (span) span.textContent = "";
        }
        field.classList.remove("is-invalid");
    };

    // Show/hide a question wrapper using its data-field-id and optionally clear + unrequire children
    const toggleWrapper = (fieldId, show) => {
        const wrap = document.querySelector(`[data-field-id="${fieldId}"]`);
        if (!wrap) return;
        wrap.classList.toggle("d-none", !show);
        if (!show) {
            $$("input, textarea, select", wrap).forEach((inp) => {
                setRequired(inp, false);
                if (inp.type === "checkbox" || inp.type === "radio") {
                    inp.checked = false;
                } else {
                    inp.value = "";
                }
                clearFieldError(inp);
            });
        }
    };

    // Convenience helpers to set required on by element id
    const req = (id, required) =>
        setRequired(document.getElementById(id), required);
    const show = (fieldId, on = true) => toggleWrapper(fieldId, on);

    // Toggle submit button enabled/disabled state with optional tooltip reason
    const setSubmitDisabled = (disabled, reason = "") => {
        const btn = document.getElementById("submit-claim");
        if (!btn) return;
        btn.disabled = !!disabled;
        if (disabled && reason) {
            btn.setAttribute("title", reason);
            btn.setAttribute("aria-disabled", "true");
        } else {
            btn.removeAttribute("title");
            btn.removeAttribute("aria-disabled");
        }
    };

    // Apply class period date bounds to relevant dynamic tables and their rows
    const applyDateLimits = () => {
        // NOTE: Per requirement, remove constraints for "Date of Application".
        // Employment status (Table 3) — match base and suffixed names
        $$('input[name^="trx-date-employment-status"]').forEach((el) =>
            setDateBounds(el, CLASS_MIN_DATE, "")
        );
        // Unemployment (Table 4) — match base and suffixed names
        $$('input[name^="trx-date-unemployment"]').forEach((el) =>
            setDateBounds(el, CLASS_MIN_DATE, "")
        );
    };

    // Attach validation to newly added transaction rows and re-apply date limits
    const bindValidationToRow = (row) => {
        if (!row) return;
        $$("[data-validate]", row).forEach((field) => {
            field.addEventListener("change", () => validateField(field));
        });
    };

    // Ensure new rows get date bounds and validation bindings across all transaction tables
    $$('[id^="add_transactions_"]').forEach((btn) => {
        btn.addEventListener("click", () => {
            const tableId =
                btn.getAttribute("tableid") || btn.id.split("_").pop();
            // Wait for core to append the row, then bind
            setTimeout(() => {
                applyDateLimits();
                const tbody = document.querySelector(
                    `#transactions_${tableId} tbody`
                );
                const lastRow = tbody
                    ? tbody.querySelector("tr:last-child")
                    : null;
                bindValidationToRow(lastRow);
                // Also bind disability-specific listeners if table 1
                if (String(tableId) === "1" && lastRow)
                    bindDisabilityRowListeners(lastRow);
                if (String(tableId) === "1") validateDisabilityTable();
                // Application table (Table 2)
                if (String(tableId) === "2" && lastRow)
                    bindApplicationRowListeners(lastRow);
                if (String(tableId) === "2") validateApplicationTable();
                // Employment table (Table 3)
                if (String(tableId) === "3" && lastRow) {
                    bindEmploymentRowListeners(lastRow);
                    // Make new employment row fields required
                    $$(
                        'input[name^="trx-date-employment-status"], input[name^="trx-text-employer"], input[name^="trx-text-job-title"], input[name^="trx-number-salary"], input[name^="trx-text-reason-departure"]',
                        lastRow
                    ).forEach((field) => {
                        setRequired(field, true);
                    });
                }
                if (String(tableId) === "3") validateEmploymentTable();
                // Unemployment table (Table 4)
                if (String(tableId) === "4") unemploymentReq.validate();
            }, 0);
        });
    });
    applyDateLimits();

    // Veteran disability rating must be >= 30
    const ratingInput = document.getElementById("number-1753900141270-0");
    if (ratingInput) {
        ratingInput.setAttribute("min", "30");
        // Ensure numeric validation is active
        const parts = (ratingInput.getAttribute("data-validate") || "")
            .split(" ")
            .filter(Boolean);
        if (!parts.includes("integer") && ratingInput.type === "number")
            parts.push("integer");
        ratingInput.setAttribute("data-validate", parts.join(" "));
    }

    // Mark baseline required fields (paper form requireds)
    const baselineRequired = [
        "select-certification-of-status",
        "select-employment-with-bop",
        "select-bop-exacerbate-condition",
        "select-medical-professional-consultation",
        "date-symptoms-started",
        "textarea-symptoms-duration",
        "select-medication-prescribed",
        "textarea-additional-effects",
        "select-relationship-affected",
        "textarea-steps-to-find-employment",
        "select-earnings-difference",
        "select-financial-problems",
        "select-spoke-with-counsel",
        "select-contact-eeo-office",
        "textarea-other-contributions",
        "textarea-anything-else",
    ];
    baselineRequired.forEach((id) => req(id, true));

    // --- Disability Rating table enforcement (Table 1) ---
    // Error node
    const disabilityWrapper = document.querySelector(
        '[data-field-id="__wrapper_6"]'
    );
    let disabilityError = document.getElementById("disability-table-error");
    if (!disabilityError && disabilityWrapper) {
        disabilityError = document.createElement("div");
        disabilityError.id = "disability-table-error";
        disabilityError.className = "text-danger fw-bold small mt-1 d-none";
        disabilityError.setAttribute("role", "alert");
        disabilityError.textContent =
            "Please enter at least one disability rating row (Disability, Percentage, and VA rating date).";
        disabilityWrapper.appendChild(disabilityError);
    }

    const setDisabilityError = (on) => {
        if (!disabilityError) return;
        disabilityError.classList.toggle("d-none", !on);
    };

    const hasAtLeastOneDisabilityRow = () => {
        const table = document.querySelector("#transactions_1 tbody");
        if (!table) return true; // if table missing, do not block
        const rows = Array.from(table.querySelectorAll("tr.transaction-row"));
        // Consider a row valid if any of its three fields has a value
        return rows.some((row) => {
            const dis = row.querySelector('input[name^="trx-text-disability"]');
            const pct = row.querySelector(
                'input[name^="trx-number-percentage"]'
            );
            const date = row.querySelector('input[name^="trx-date-va-rating"]');
            return [dis, pct, date].some(
                (f) => f && String(f.value).trim() !== ""
            );
        });
    };

    const validateDisabilityTable = () => {
        const vet = document.getElementById("select-certification-of-status");
        const vetYes = vet && vet.value.toUpperCase() === "YES";
        const ok = !vetYes || hasAtLeastOneDisabilityRow();
        setDisabilityError(!ok);
        return ok;
    };

    const bindDisabilityRowListeners = (row) => {
        if (!row) return;
        ["input", "change"].forEach((evt) => {
            row.addEventListener(evt, validateDisabilityTable, {
                passive: true,
            });
        });
    };

    // Bind listeners for existing first row
    const firstDisRow = document.querySelector(
        "#transactions_1 tbody tr.transaction-row"
    );
    if (firstDisRow) bindDisabilityRowListeners(firstDisRow);
    // Also revalidate when removing rows
    const removeDisBtn = document.getElementById("remove_transactions_1");
    if (removeDisBtn)
        removeDisBtn.addEventListener("click", () =>
            setTimeout(validateDisabilityTable, 0)
        );

    // --- Application table enforcement (Table 2) ---
    const applicationWrapper = document.querySelector(
        '[data-field-id="__wrapper_10"]'
    );
    let applicationError = document.getElementById("application-table-error");
    if (!applicationError && applicationWrapper) {
        applicationError = document.createElement("div");
        applicationError.id = "application-table-error";
        applicationError.className = "text-danger fw-bold small mt-1 d-none";
        applicationError.setAttribute("role", "alert");
        applicationError.textContent =
            "You must enter at least one application row.";
        applicationWrapper.appendChild(applicationError);
    }

    const setApplicationError = (on) => {
        if (!applicationError) return;
        applicationError.classList.toggle("d-none", !on);
    };

    const hasAtLeastOneApplicationRow = () => {
        const table = document.querySelector("#transactions_2 tbody");
        if (!table) return true;
        const rows = Array.from(table.querySelectorAll("tr.transaction-row"));
        return rows.some((row) => {
            const date = row.querySelector(
                'input[name^="trx-date-application"]'
            );
            const inst = row.querySelector(
                'input[name^="trx-text-institution"]'
            );
            const job = row.querySelector('input[name^="trx-text-job"]');
            const cond = row.querySelector(
                'select[name^="trx-select-conditional-offer"]'
            );
            const wd = row.querySelector(
                'select[name^="trx-select-withdraw-offer"]'
            );
            const nonEmpty = (el) => el && String(el.value).trim() !== "";
            return [date, inst, job, cond, wd].some(nonEmpty);
        });
    };

    const validateApplicationTable = () => {
        const applied = document.getElementById("select-employment-with-bop");
        const need = applied && applied.value.toUpperCase() === "YES";
        const ok = !need || hasAtLeastOneApplicationRow();
        setApplicationError(!ok);
        return ok;
    };

    const bindApplicationRowListeners = (row) => {
        if (!row) return;
        ["input", "change"].forEach((evt) => {
            row.addEventListener(evt, validateApplicationTable, {
                passive: true,
            });
        });
    };

    // Bind first row
    const firstAppRow = document.querySelector(
        "#transactions_2 tbody tr.transaction-row"
    );
    if (firstAppRow) bindApplicationRowListeners(firstAppRow);
    const removeAppBtn = document.getElementById("remove_transactions_2");
    if (removeAppBtn)
        removeAppBtn.addEventListener("click", () =>
            setTimeout(validateApplicationTable, 0)
        );

    // --- Employment table enforcement (Table 3) ---
    const employmentWrapper = document.querySelector(
        '[data-field-id="__wrapper_55"]'
    );
    let employmentError = document.getElementById("employment-table-error");
    if (!employmentError && employmentWrapper) {
        employmentError = document.createElement("div");
        employmentError.id = "employment-table-error";
        employmentError.className = "text-danger fw-bold small mt-1 d-none";
        employmentError.setAttribute("role", "alert");
        employmentError.textContent =
            "Please enter at least one employment status row.";
        employmentWrapper.appendChild(employmentError);
    }

    const setEmploymentError = (on) => {
        if (!employmentError) return;
        employmentError.classList.toggle("d-none", !on);
    };

    const hasAtLeastOneEmploymentRow = () => {
        const table = document.querySelector("#transactions_3 tbody");
        if (!table) return true;
        const rows = Array.from(table.querySelectorAll("tr.transaction-row"));
        return rows.some((row) => {
            const date = row.querySelector(
                'input[name^="trx-date-employment-status"]'
            );
            const employer = row.querySelector(
                'input[name^="trx-text-employer"]'
            );
            const jobTitle = row.querySelector(
                'input[name^="trx-text-job-title"]'
            );
            const salary = row.querySelector(
                'input[name^="trx-number-salary"]'
            );
            const reason = row.querySelector(
                'input[name^="trx-text-reason-departure"]'
            );
            const nonEmpty = (el) => el && String(el.value).trim() !== "";
            return [date, employer, jobTitle, salary, reason].some(nonEmpty);
        });
    };

    const validateEmploymentTable = () => {
        // Employment table is always required - no gating condition
        const ok = hasAtLeastOneEmploymentRow();
        setEmploymentError(!ok);
        return ok;
    };

    const bindEmploymentRowListeners = (row) => {
        if (!row) return;
        ["input", "change"].forEach((evt) => {
            row.addEventListener(evt, validateEmploymentTable, {
                passive: true,
            });
        });
    };

    // Bind first row
    const firstEmpRow = document.querySelector(
        "#transactions_3 tbody tr.transaction-row"
    );
    if (firstEmpRow) bindEmploymentRowListeners(firstEmpRow);
    const removeEmpBtn = document.getElementById("remove_transactions_3");
    if (removeEmpBtn)
        removeEmpBtn.addEventListener("click", () =>
            setTimeout(validateEmploymentTable, 0)
        );

    // Make employment table fields required (always required - no gating condition)
    $$(
        'input[name^="trx-date-employment-status"], input[name^="trx-text-employer"], input[name^="trx-text-job-title"], input[name^="trx-number-salary"], input[name^="trx-text-reason-departure"]'
    ).forEach((field) => {
        setRequired(field, true);
    });

    // --- Generic: Require at least one row for a table (DRY helper) ---
    const requireTableAtLeastOneRow = ({
        tableId,
        wrapperFieldId,
        errorId,
        errorText,
        enforce = () => true,
        rowSelectors = [],
        noneControl = null,
    }) => {
        const wrapper = document.querySelector(
            `[data-field-id="${wrapperFieldId}"]`
        );
        if (!wrapper) return () => true;

        // Error container
        let err = document.getElementById(errorId);
        if (!err) {
            err = document.createElement("div");
            err.id = errorId;
            err.className = "text-danger fw-bold small mt-1 d-none";
            err.setAttribute("role", "alert");
            err.textContent = errorText;
            wrapper.appendChild(err);
        }

        // Optional "None" control for skipping requirement
        let noneEl = null;
        if (noneControl && !document.getElementById(noneControl.id)) {
            const grp = document.createElement("div");
            grp.className = "form-check mb-2";
            noneEl = document.createElement("input");
            noneEl.type = "checkbox";
            noneEl.className = "form-check-input";
            noneEl.id = noneControl.id;
            const lbl = document.createElement("label");
            lbl.className = "form-check-label";
            lbl.setAttribute("for", noneControl.id);
            lbl.textContent = noneControl.label;
            grp.appendChild(noneEl);
            grp.appendChild(lbl);
            // Insert at top of wrapper
            wrapper.insertBefore(grp, wrapper.firstChild);
        } else if (noneControl) {
            noneEl = document.getElementById(noneControl.id);
        }

        const tableBody = () =>
            document.querySelector(`#transactions_${tableId} tbody`);
        const tableNodes = () => ({
            table: document.getElementById(`table_responsive_${tableId}`),
            ctrl: document.getElementById(`trx_ctrl_${tableId}`),
        });

        const clearTable = () => {
            const tb = tableBody();
            if (!tb) return;
            tb.querySelectorAll("tr.transaction-row").forEach((row) => {
                $$("input, select, textarea", row).forEach((el) => {
                    setRequired(el, false);
                    if (el.type === "checkbox" || el.type === "radio")
                        el.checked = false;
                    else el.value = "";
                    clearFieldError(el);
                });
            });
        };

        const setTableVisible = (visible) => {
            const { table, ctrl } = tableNodes();
            if (table) table.classList.toggle("d-none", !visible);
            if (ctrl) ctrl.classList.toggle("d-none", !visible);
        };

        const hasAtLeastOneRow = () => {
            const tb = tableBody();
            if (!tb) return true;
            const rows = Array.from(tb.querySelectorAll("tr.transaction-row"));
            return rows.some((row) =>
                rowSelectors.some((sel) => {
                    const el = row.querySelector(sel);
                    return el && String(el.value).trim() !== "";
                })
            );
        };

        const setError = (on) => err && err.classList.toggle("d-none", !on);

        const validate = () => {
            // Gating: if none control checked, skip; else use enforce()
            const skip = noneEl && noneEl.checked;
            const must = !skip && enforce();
            const ok = !must || hasAtLeastOneRow();
            setError(!ok);
            return ok;
        };

        // Bind existing first row
        const firstRow = tableBody()
            ? tableBody().querySelector("tr.transaction-row")
            : null;
        if (firstRow) {
            ["input", "change"].forEach((evt) => {
                firstRow.addEventListener(evt, validate, { passive: true });
            });
        }

        // Bind remove
        const removeBtn = document.getElementById(
            `remove_transactions_${tableId}`
        );
        if (removeBtn)
            removeBtn.addEventListener("click", () => setTimeout(validate, 0));

        // Bind none control
        if (noneEl) {
            noneEl.addEventListener("change", () => {
                const visible = !noneEl.checked;
                setTableVisible(visible);
                if (!visible) clearTable();
                validate();
            });
        }

        // Initial state
        validate();
        return { validate };
    };

    // Unemployment (Table 4): enforce unless "No unemployment periods" is checked
    const unemploymentReq = requireTableAtLeastOneRow({
        tableId: 4,
        wrapperFieldId: "__wrapper_57",
        errorId: "unemployment-table-error",
        errorText:
            "Please enter at least one unemployment period, or check the box to indicate none.",
        enforce: () => true,
        rowSelectors: [
            'input[name^="trx-date-unemployment"]',
            'input[name^="trx-text-reason-unemployment"]',
        ],
        noneControl: {
            id: "no_unemployment_periods",
            label: "I did not have any unemployment periods during this time",
        },
    });

    const showModal = (title, body) => {
        const fn =
            typeof window !== "undefined" && typeof window.modal === "function"
                ? window.modal
                : typeof modal === "function"
                  ? modal
                  : null;
        if (fn) fn(title, body);
        else window.alert(`${title}\n\n${body}`);
    };

    // Rule engine: map controlling select -> handlers keyed by value
    const rules = [
        {
            control: "select-certification-of-status",
            required: true,
            handlers: {
                YES: ({ fromUser = false } = {}) => {
                    // Show and require service details and rating
                    show("_select-branch-of-military", true);
                    show("_textarea-dates-of-enlistment", true);
                    show("_number-1753900141270-0", true);
                    show("__wrapper_5", true); // conditions explanation text
                    show("__wrapper_6", true); // disability rating table
                    req("select-branch-of-military", true);
                    req("textarea-dates-of-enlistment", true);
                    if (ratingInput) req("number-1753900141270-0", true);
                    // Enable submit
                    setSubmitDisabled(false);
                },
                NO: ({ fromUser = false } = {}) => {
                    // Hide and clear veteran-specific fields
                    show("_select-branch-of-military", false);
                    show("_textarea-dates-of-enlistment", false);
                    show("_number-1753900141270-0", false);
                    show("__wrapper_5", false);
                    show("__wrapper_6", false);
                    // Disable submit until eligibility is corrected
                    setSubmitDisabled(
                        true,
                        "Submission disabled: eligibility requires veteran status = YES"
                    );
                    // Inform ineligible, allow correction if user explicitly chose NO
                    if (fromUser) {
                        showModal(
                            "Not Eligible",
                            "Based on your answer, you are not eligible to submit this claim. If this is incorrect, change your answer to YES to continue."
                        );
                    }
                },
                default: () => {
                    // Treat empty as NO for gating behavior
                    // Do not disable submit on initial unset value
                    // Keep veteran-only fields hidden but allow interaction
                    show("_select-branch-of-military", false);
                    show("_textarea-dates-of-enlistment", false);
                    show("_number-1753900141270-0", false);
                    show("__wrapper_5", false);
                    show("__wrapper_6", false);
                    setSubmitDisabled(false);
                },
            },
        },
        {
            control: "select-employment-with-bop",
            required: true,
            handlers: {
                YES: () => {
                    // Application table and follow-ups visible + required
                    show("__wrapper_9", true); // informational callout above application table
                    show("__wrapper_10", true); // application table placeholder
                    show("_textarea-bop-withdrew-reasons", true);
                    show("_textarea-bop-employment-status", true);
                    show("_select-bop-termination", true);
                    show("_select-bop-opm-review", true);
                    show("_select-bop-opm-notify-you", true);
                    show("_select-bop-opm-right-to-submit", true);
                    req("textarea-bop-withdrew-reasons", true);
                    req("textarea-bop-employment-status", true);
                    req("select-bop-termination", true);
                    req("select-bop-opm-review", true);
                    req("select-bop-opm-notify-you", true);
                    req("select-bop-opm-right-to-submit", true);
                    // Make application table fields required
                    $$(
                        'input[name^="trx-date-application"], input[name^="trx-text-institution"], input[name^="trx-text-job"], select[name^="trx-select-conditional-offer"], select[name^="trx-select-withdraw-offer"]'
                    ).forEach((field) => {
                        setRequired(field, true);
                    });
                    // Validate application table requirement
                    validateApplicationTable();
                    // Re-enable submit if previously disabled for NO
                    setSubmitDisabled(false);
                },
                NO: ({ fromUser = false } = {}) => {
                    // Hide BOP follow-ups when no employment application
                    show("__wrapper_9", false);
                    show("__wrapper_10", false);
                    show("_textarea-bop-withdrew-reasons", false);
                    show("_textarea-bop-employment-status", false);
                    show("_select-bop-termination", false);
                    show("_textarea-bop-reason-for-termination", false);
                    show("_select-bop-opm-review", false);
                    show("_select-bop-opm-notify-you", false);
                    show("_select-bop-opm-right-to-submit", false);
                    // Remove required from application table fields
                    $$(
                        'input[name^="trx-date-application"], input[name^="trx-text-institution"], input[name^="trx-text-job"], select[name^="trx-select-conditional-offer"], select[name^="trx-select-withdraw-offer"]'
                    ).forEach((field) => {
                        setRequired(field, false);
                    });
                    // clear application error
                    validateApplicationTable();
                    // Disable submit and inform ineligible
                    setSubmitDisabled(
                        true,
                        "Submission disabled: eligibility requires that you applied to the BOP"
                    );
                    if (fromUser) {
                        showModal(
                            "Not Eligible",
                            "Based on your answer, you are not eligible to submit this claim. If this is incorrect, change your answer to YES to continue."
                        );
                    }
                },
                default: () => {
                    // Initial/empty state: hide related follow-ups, but do not disable submit
                    show("__wrapper_9", false);
                    show("__wrapper_10", false);
                    show("_textarea-bop-withdrew-reasons", false);
                    show("_textarea-bop-employment-status", false);
                    show("_select-bop-termination", false);
                    show("_textarea-bop-reason-for-termination", false);
                    show("_select-bop-opm-review", false);
                    show("_select-bop-opm-notify-you", false);
                    show("_select-bop-opm-right-to-submit", false);
                    validateApplicationTable();
                },
            },
        },
        {
            control: "select-bop-termination",
            required: false,
            handlers: {
                YES: () => {
                    show("_textarea-bop-reason-for-termination", true);
                    req("textarea-bop-reason-for-termination", true);
                },
                NO: () => {
                    show("_textarea-bop-reason-for-termination", false);
                },
                default: () => {
                    rules[2].handlers.NO();
                },
            },
        },
        {
            control: "select-bop-exacerbate-condition",
            required: true,
            handlers: {
                YES: () => {
                    show("_textarea-preexisting-condition", true);
                    show("_textarea-preexisting-condition-explanation", true);
                    req("textarea-preexisting-condition", true);
                    req("textarea-preexisting-condition-explanation", true);
                },
                NO: () => {
                    show("_textarea-preexisting-condition", false);
                    show("_textarea-preexisting-condition-explanation", false);
                },
                default: () => {
                    rules[3].handlers.NO();
                },
            },
        },
        {
            control: "select-medical-professional-consultation",
            required: true,
            handlers: {
                YES: () => {
                    show("_number-oop-for-counseling", true);
                    show("_textarea-new-condition-diagnosed", true);
                    req("number-oop-for-counseling", true);
                    req("textarea-new-condition-diagnosed", true);
                    // Set max value to $10,000 for out-of-pocket counseling costs
                    const oopField = document.getElementById(
                        "number-oop-for-counseling"
                    );
                    if (oopField) {
                        oopField.setAttribute("max", "10000");
                    }
                },
                NO: () => {
                    show("_number-oop-for-counseling", false);
                    show("_textarea-new-condition-diagnosed", false);
                },
                default: () => {
                    rules[4].handlers.NO();
                },
            },
        },
        {
            control: "select-medication-prescribed",
            required: true,
            handlers: {
                YES: () => {
                    show("_number-oop-for-medication", true);
                    req("number-oop-for-medication", true);
                    // Set max value to $10,000 for out-of-pocket medication costs
                    const medField = document.getElementById(
                        "number-oop-for-medication"
                    );
                    if (medField) {
                        medField.setAttribute("max", "10000");
                    }
                },
                NO: () => {
                    show("_number-oop-for-medication", false);
                },
                default: () => {
                    rules[5].handlers.NO();
                },
            },
        },
        {
            control: "select-relationship-affected",
            required: true,
            handlers: {
                YES: () => {
                    show("_textarea-relationship-affected-description", true);
                    req("textarea-relationship-affected-description", true);
                },
                NO: () => {
                    show("_textarea-relationship-affected-description", false);
                },
                default: () => {
                    rules[6].handlers.NO();
                },
            },
        },
        {
            control: "select-earnings-difference",
            required: true,
            handlers: {
                YES: () => {
                    show("_textarea-earnings-difference-explanation", true);
                    show("_textarea-earnings-difference-amount", true);
                    show("_textarea-loss-of-compensation", true);
                    req("textarea-earnings-difference-explanation", true);
                    req("textarea-earnings-difference-amount", true);
                    req("textarea-loss-of-compensation", true);
                },
                NO: () => {
                    show("_textarea-earnings-difference-explanation", false);
                    show("_textarea-earnings-difference-amount", false);
                    show("_textarea-loss-of-compensation", false);
                },
                default: () => {
                    rules[7].handlers.NO();
                },
            },
        },
        {
            control: "select-financial-problems",
            required: true,
            handlers: {
                YES: () => {
                    show("_textarea-financial-problems-description", true);
                    req("textarea-financial-problems-description", true);
                },
                NO: () => {
                    show("_textarea-financial-problems-description", false);
                },
                default: () => {
                    rules[8].handlers.NO();
                },
            },
        },
        {
            control: "select-spoke-with-counsel",
            required: true,
            handlers: {
                YES: () => {
                    show("_textarea-spoke-with-counsel-description", true);
                    req("textarea-spoke-with-counsel-description", true);
                },
                NO: () => {
                    show("_textarea-spoke-with-counsel-description", false);
                },
                default: () => {
                    rules[9].handlers.NO();
                },
            },
        },
        {
            control: "select-contact-eeo-office",
            required: true,
            handlers: {
                YES: () => {
                    show("_textarea-contact-eeo-office-description", true);
                    req("textarea-contact-eeo-office-description", true);
                },
                NO: () => {
                    show("_textarea-contact-eeo-office-description", false);
                },
                default: () => {
                    rules[10].handlers.NO();
                },
            },
        },
    ];

    // Attach handlers and run once to set initial state
    rules.forEach((rule) => {
        const ctl = document.getElementById(rule.control);
        if (!ctl) return;
        if (rule.required) setRequired(ctl, true);
        const dispatch = (fromUser = false) => {
            const val = (ctl.value || "").toUpperCase();
            const handler = rule.handlers[val] || rule.handlers.default;
            if (typeof handler === "function") handler({ fromUser });
        };
        ctl.addEventListener("change", () => {
            dispatch(true);
            if (rule.control === "select-certification-of-status") {
                // Update disability table requirement visibility based on eligibility
                validateDisabilityTable();
            }
        });
        // Initial: apply veteran-specific defaults (do not hide the rest of the form)
        dispatch(false);
    });

    // Always-required textareas that don’t depend on a controlling select
    [
        "date-symptoms-started",
        "textarea-symptoms-duration",
        "textarea-additional-effects",
        "textarea-steps-to-find-employment",
        "textarea-other-contributions",
        "textarea-anything-else",
    ].forEach((id) => req(id, true));

    // Shorten data-label attributes for specific fields with long labels
    try {
        const labelUpdates = [
            {
                id: "select-bop-opm-right-to-submit",
                label: "Did the BOP notify you of your right to submit information to OPM?",
            },
            {
                id: "textarea-additional-effects",
                label: "Additional information about negative health effects",
            },
            {
                id: "textarea-financial-problems-description",
                label: "Describe the financial problems you experienced",
            },
            {
                id: "textarea-additional-information",
                label: "Additional information about the BOP's decision",
            },
            {
                id: "date-symptoms-started",
                label: "Approximate date when you first started experiencing symptoms",
            },
            {
                id: "select-relationship-affected",
                label: "Has the BOP's decision negatively affected your relationships?",
            },
            {
                id: "textarea-relationship-affected-description",
                label: "Describe how the BOP's decision affected your relationships",
            },
            {
                id: "textarea-anything-else",
                label: "Other information regarding your BOP application or employment",
            },
        ];

        labelUpdates.forEach(({ id, label }) => {
            const element = document.getElementById(id);
            if (element) {
                element.setAttribute("data-label", label);
            }
        });
    } catch {
        // Silent fail - labels will retain original text if update fails
    }
    // END AGENT

    // Replace static '*' in labels with styled required-asterisk span and
    // keep it in sync with actual required state.
    try {
        const allLabels = document.querySelectorAll("form#form label[for]");
        allLabels.forEach((label) => {
            // Remove any trailing '*' from the label text nodes only
            Array.from(label.childNodes).forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    node.nodeValue = node.nodeValue.replace(/\*\s*$/, "");
                }
            });
            const forId = label.getAttribute("for");
            const field = document.getElementById(forId);
            if (!field) return;
            const isRequired =
                field.hasAttribute("required") ||
                (field.getAttribute("data-validate") || "")
                    .split(" ")
                    .includes("required");
            updateLabelAsterisk(field, isRequired);
        });
    } catch (e) {
        console.warn("Required asterisk normalization failed", e);
    }

    // Inject helper text for phone fields (Home and Cell Phone)
    try {
        const addHelper = (containerId, inputSelector, text) => {
            const wrap = document.getElementById(containerId);
            if (!wrap) return;
            const input = wrap.querySelector(inputSelector);
            if (!input) return;
            const helpId = `${containerId}-help`;
            if (!document.getElementById(helpId)) {
                const help = document.createElement("div");
                help.className = "form-text text-muted mt-1";
                help.id = helpId;
                help.textContent = text;
                wrap.appendChild(help);
            }
            // Link helper for accessibility
            const desc = (input.getAttribute("aria-describedby") || "")
                .split(" ")
                .filter(Boolean);
            if (!desc.includes(helpId)) {
                desc.push(helpId);
                input.setAttribute("aria-describedby", desc.join(" "));
            }
        };

        const helperCopy =
            "If your home and cell phones are the same, please enter that number in the blanks for both phones.";
        addHelper("divContactField_12", "#phone_number_1", helperCopy);
        addHelper("divContactField_13", "#phone_number_2", helperCopy);
    } catch (e) {
        console.warn("Adding phone helper text failed", e);
    }

    /**
     * Class member has made a claim and returning to edit claim
     */
    if (claim?.data?.claim_id) {
        // Populate fields
        Array.from(claim.class_member.claim_fields).forEach((field) => {
            if (
                field.name ===
                    "Please state the branch(es) of the military in which you served." &&
                field.value
            ) {
                try {
                    // Parse the JSON value to extract branch selections
                    const branchData = JSON.parse(field.value);
                    const branchInput = document.getElementById(
                        "select-branch-of-military"
                    );

                    if (branchInput && Array.isArray(branchData)) {
                        // Get the Tagify instance (should already be initialized in customInitClaim)
                        const tagifyInstance = branchInput.tagify;

                        if (tagifyInstance) {
                            // Extract values and add them to Tagify
                            const tags = branchData
                                .map((item) => item.value)
                                .filter(Boolean);
                            tagifyInstance.addTags(tags);

                            // Update the underlying input value for form submission
                            branchInput.value = field.value;
                        }
                    }
                } catch {
                    // Silent fail - field will remain empty if parsing fails
                }
            }

            // Populate shortened data-label fields
            const shortenedLabels = [
                "Did the BOP notify you of your right to submit information to OPM?",
                "Additional information about negative health effects",
                "Describe the financial problems you experienced",
                "Additional information about the BOP's decision",
                "Approximate date when you first started experiencing symptoms",
                "Has the BOP's decision negatively affected your relationships?",
                "Describe how the BOP's decision affected your relationships",
                "Other information regarding your BOP application or employment",
            ];

            if (shortenedLabels.includes(field.name) && field.value) {
                // Map field names to their corresponding input IDs
                const fieldMap = {
                    "Did the BOP notify you of your right to submit information to OPM?":
                        "select-bop-opm-right-to-submit",
                    "Additional information about negative health effects":
                        "textarea-additional-effects",
                    "Describe the financial problems you experienced":
                        "textarea-financial-problems-description",
                    "Additional information about the BOP's decision":
                        "textarea-additional-information",
                    "Approximate date when you first started experiencing symptoms":
                        "date-symptoms-started",
                    "Has the BOP's decision negatively affected your relationships?":
                        "select-relationship-affected",
                    "Describe how the BOP's decision affected your relationships":
                        "textarea-relationship-affected-description",
                    "Other information regarding your BOP application or employment":
                        "textarea-anything-else",
                };

                const inputId = fieldMap[field.name];
                if (inputId) {
                    const element = document.getElementById(inputId);
                    if (element) {
                        element.value = field.value;
                    }
                }
            }
            // AGENT END
        });
    }

    return claim;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    console.log("[Load class member]...");
    // custom code goes here
    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    console.log("[Process claim]...");

    // Enforce: at least one Disability Rating row when veteran = YES
    try {
        const vet = document.getElementById("select-certification-of-status");
        if (vet && vet.value.toUpperCase() === "YES") {
            const ok =
                typeof validateDisabilityTable === "function"
                    ? validateDisabilityTable()
                    : true;
            if (!ok) {
                claim.flags.valid = false;
                // Scroll into view to aid correction
                const anchor = document.querySelector(
                    '[data-field-id="__wrapper_6"]'
                );
                if (anchor && anchor.scrollIntoView)
                    anchor.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
            }
        }
    } catch (e) {
        console.warn("Disability table validation error", e);
    }

    // Enforce: at least one Application row when applied to BOP = YES
    try {
        const applied = document.getElementById("select-employment-with-bop");
        if (applied && applied.value.toUpperCase() === "YES") {
            const ok =
                typeof validateApplicationTable === "function"
                    ? validateApplicationTable()
                    : true;
            if (!ok) {
                claim.flags.valid = false;
                const anchor = document.querySelector(
                    '[data-field-id="__wrapper_10"]'
                );
                if (anchor && anchor.scrollIntoView)
                    anchor.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
            }
        } else if (applied && applied.value.toUpperCase() === "NO") {
            // Hard-guard: not eligible if never applied to the BOP
            claim.flags.valid = false;
            const anchor = document.querySelector(
                '[data-field-id="_select-employment-with-bop"]'
            );
            if (anchor && anchor.scrollIntoView)
                anchor.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    } catch (e) {
        console.warn("Application table validation error", e);
    }

    // Enforce: Supporting Documentation required when earnings difference = YES
    try {
        const earningsDiff = document.getElementById(
            "select-earnings-difference"
        );
        if (earningsDiff && earningsDiff.value.toUpperCase() === "YES") {
            // Check for uploaded files
            const hasFiles =
                (claim.uploads && claim.uploads.length > 0) ||
                (claim.data?.claim_id &&
                    claim.class_member?.claim_files &&
                    claim.class_member.claim_files.length > 0);

            if (!hasFiles) {
                claim.flags.valid = false;
                modal(
                    "Supporting Documentation Required",
                    "You indicated that you believe you earned less at a subsequent job than what you would have earned at the BOP. Please upload supporting documentation to proceed with your claim submission."
                );
            }
        }
    } catch (e) {
        console.warn("Supporting documentation validation error", e);
    }

    // Paper-claim submissions (Ops): tag the source as paper/hard-copy, and
    // keep the Ops-entered signature date instead of the auto-set timestamp.
    const isPaperClaim =
        new URLSearchParams(window.location.search).get("paper_claim") ===
        "true";
    claim.data.contact.ag_PAPER = isPaperClaim;
    if (isPaperClaim) {
        const dateField = document.getElementById("date");
        if (dateField) claim.data.contact.date = dateField.value;
    }

    return claim;
}
